/* eslint-disable no-param-reassign */
import { createAction, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { RootState, ThunkAction } from '~/store/types';
import { ACTION_PREFIX } from './sportsbook-constants';
import { fetchActivateBonusTokens, fetchOperatorConfigurations } from './api/sportsbook-api';
import { getSportradarLocale } from './utils/sportradar-utils';
import { selectIsoCodeForLocale } from '~/components/brand/ducks/selectors';
import { createSelector } from 'reselect';
import { ConfigurationSourceDto, isArlandSource } from './api/sbapi/sbapi-api-model';
import selectSportsbookState from './sportsbook-state-selector';
import reportSportsbookError from './utils/sportsbook-error-reporter';

type State = {
	sources: Array<ConfigurationSourceDto>;
	statisticsUrl?: string;
	betGeniusGameTrackerBaseUrl: string | null;
	isInitialized: boolean;
	widgetScriptUrl: string;
	sportradarLocale: string;
	oddsFormat: string | null;
};

const LOCALE_PATTERN = '{LOCALE}';
const WIDGET_SCRIPT_URL = 'https://widgets.sir.sportradar.com/d7130cb5c29775a1f1c23ffddf4f1467/widgetloader';
const PREFERRED_ODDS_FORMAT_KEY = 'preferredOddsFormat';

// Actions
const ACTIVATE_BONUS_COMPLETED = `${ACTION_PREFIX}CONFIGURATION/ACTIVATE_BONUS_COMPLETED`;
type ActivateBonusCompletedAction = { type: typeof ACTIVATE_BONUS_COMPLETED };

// Selectors
export const selectState = (state: RootState) => selectSportsbookState(state).configuration;
const selectArlandSource = (state: RootState) => selectState(state).sources.filter(isArlandSource)[0] || undefined;
export const selectArlandNodeId = createSelector(selectArlandSource, source => source?.arland?.nodeId);
export const selectIsConfigurationInitialized = (state: RootState) => selectState(state).isInitialized;
// configuration data
export const selectStatisticsUrl = (state: RootState) => selectState(state).statisticsUrl;
export const selectBetGeniusGameTrackerBaseUrl = (state: RootState) => selectState(state).betGeniusGameTrackerBaseUrl;
export const selectSportradarLocale = (state: RootState) => selectState(state).sportradarLocale;
export const selectOddsFormat = (state: RootState) => selectSportsbookState(state).configuration?.oddsFormat;
// other data
export const selectWidgetScriptUrl = (state: RootState) => selectState(state).widgetScriptUrl;

/*
	Call to init arland free bet tokens, risk free tokens and bonus boost tokens
 */
export const activateBonusTokens = (): ThunkAction<ActivateBonusCompletedAction> => async dispatch => {
	try {
		await fetchActivateBonusTokens();
		dispatch({ type: ACTIVATE_BONUS_COMPLETED });
	} catch (error) {
		reportSportsbookError('Failed to fetch active bonus tokens', error);
	}
};

// Reducer
const initialState: State = {
	sources: [],
	statisticsUrl: undefined,
	betGeniusGameTrackerBaseUrl: null,
	isInitialized: false,
	widgetScriptUrl: WIDGET_SCRIPT_URL,
	sportradarLocale: '',
	oddsFormat: window?.localStorage?.getItem(PREFERRED_ODDS_FORMAT_KEY)
};

const handleConfigurationCompleted = (
	state: State,
	action: PayloadAction<{
		sources: Array<ConfigurationSourceDto>;
		statisticsUrl: string;
		betGeniusGameTrackerBaseUrl: string | null;
		sportradarLocale: string;
	}>
) => {
	state.sources = action.payload.sources;
	state.statisticsUrl = action.payload.statisticsUrl;
	state.betGeniusGameTrackerBaseUrl = action.payload.betGeniusGameTrackerBaseUrl;
	state.sportradarLocale = action.payload.sportradarLocale;
	state.isInitialized = true;
	state.oddsFormat = window?.localStorage?.getItem(PREFERRED_ODDS_FORMAT_KEY);
};

const handleOddsFormatUpdated = (state: State, action: PayloadAction<string>) => {
	state.oddsFormat = action.payload;
};

const configurationSlice = createSlice({
	name: 'configuration',
	initialState,
	reducers: {
		loadConfigurationCompleted: handleConfigurationCompleted,
		loadConfigurationFailed: () => initialState,
		loadConfigurationDisposed: () => initialState,
		oddsFormatUpdated: handleOddsFormatUpdated
	}
});

const { loadConfigurationCompleted, loadConfigurationFailed, oddsFormatUpdated } = configurationSlice.actions;
export const dispose = createAction('configuration/loadConfigurationDisposed');

export const updateOddsFormat =
	(newOddsFormat: string): ThunkAction =>
	dispatch => {
		window?.localStorage?.setItem(PREFERRED_ODDS_FORMAT_KEY, newOddsFormat);
		dispatch(oddsFormatUpdated(newOddsFormat));
	};

export const loadConfiguration = (): ThunkAction => async (dispatch, getState) => {
	try {
		const configuration = await fetchOperatorConfigurations();

		if (!configuration) {
			throw new Error('No configuration was received.');
		}

		const isoCode = selectIsoCodeForLocale(getState());
		const sportradarLocale = getSportradarLocale(isoCode);

		const payload = {
			sources: configuration.sources,
			statisticsUrl: configuration.statisticsUrl.replace(LOCALE_PATTERN, `${sportradarLocale}`),
			betGeniusGameTrackerBaseUrl: configuration.betGeniusGameTrackerBaseUrl,
			sportradarLocale
		};

		dispatch(loadConfigurationCompleted(payload));
	} catch (error) {
		reportSportsbookError('Error while loading Sportsbook configuration', error);
		dispatch(loadConfigurationFailed());
	}
};

export default configurationSlice.reducer;
